/**
 * AudioContext management and playback queue.
 *
 * Manages:
 * - Shared AudioContext (singleton, resumed on user gesture)
 * - TTS gain node for volume control
 * - Audio playback queue via HTMLAudioElement with preservesPitch
 * - Playback speed control (1×–2×) with proper time-stretching
 * - Pause/resume, skip, stop
 * - Mobile audio unlock via silent playback on first user gesture
 */

import { STORAGE_KEYS, SPEED_STEPS } from '../utils/constants';

// ── AudioContext Singleton ──

let audioContext: AudioContext | null = null;
let ttsGainNode: GainNode | null = null;

/**
 * Get or create the shared AudioContext.
 * Must be called from a user gesture handler for the first time.
 */
export function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new AudioContext();
    ttsGainNode = audioContext.createGain();
    ttsGainNode.connect(audioContext.destination);
  }
  return audioContext;
}

/**
 * Get the TTS gain node for volume control.
 */
export function getTtsGainNode(): GainNode {
  if (!ttsGainNode) {
    getAudioContext();
  }
  return ttsGainNode!;
}

/**
 * Ensure AudioContext is resumed (required after user gesture on mobile).
 */
export async function ensureAudioContextResumed(): Promise<void> {
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
}

/**
 * Decode raw audio data (ArrayBuffer) into an AudioBuffer.
 */
export async function decodeAudio(data: ArrayBuffer): Promise<AudioBuffer> {
  const ctx = getAudioContext();
  // Copy the ArrayBuffer because decodeAudioData detaches it
  const copy = data.slice(0);
  return ctx.decodeAudioData(copy);
}

/**
 * Duck the TTS gain (reduce volume during speech detection).
 */
export function duckTtsGain(durationMs: number = 200): void {
  const ctx = getAudioContext();
  const gain = getTtsGainNode();
  gain.gain.setValueAtTime(0.15, ctx.currentTime);
  gain.gain.linearRampToValueAtTime(
    0.15,
    ctx.currentTime + durationMs / 1000,
  );
}

/**
 * Restore the TTS gain to full volume.
 */
export function restoreTtsGain(durationMs: number = 200): void {
  const ctx = getAudioContext();
  const gain = getTtsGainNode();
  gain.gain.linearRampToValueAtTime(1.0, ctx.currentTime + durationMs / 1000);
}

// ── HTMLAudioElement Unlock (Mobile) ──

let audioUnlocked = false;

/**
 * Persistent Audio element reused for all TTS playback.
 * Created once and unlocked on first user gesture to satisfy
 * mobile autoplay policies.
 */
const persistentAudio: HTMLAudioElement = new Audio();

/** Silent WAV (44-byte header, 0 samples) for unlocking audio on mobile */
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

/**
 * Unlock the HTMLAudioElement for mobile playback.
 * Must be called from a user gesture handler (e.g., tap/click).
 * Safe to call multiple times — only the first call has an effect.
 */
export function unlockAudio(): void {
  if (audioUnlocked) return;
  audioUnlocked = true;
  persistentAudio.src = SILENT_WAV;
  persistentAudio.play().then(() => persistentAudio.pause()).catch(() => {});
}

// ── Audio Queue ──

/** Item in the playback queue */
export interface AudioQueueItem {
  /** Raw audio data (ArrayBuffer from TTS synthesis) */
  audioData: ArrayBuffer;
  /** Optional metadata tag (e.g., message ID) */
  tag?: string;
}

/** AudioQueue event callbacks */
export interface AudioQueueCallbacks {
  /** Called when playback starts (first item begins playing) */
  onPlaybackStart?: () => void;
  /** Called when playback finishes (queue fully drained) */
  onPlaybackEnd?: () => void;
  /** Called when an individual item starts playing */
  onItemStart?: (tag?: string) => void;
  /** Called when an individual item finishes playing */
  onItemEnd?: (tag?: string) => void;
  /** Debug logger */
  onLog?: (event: string, data?: Record<string, unknown>) => void;
}

/**
 * Audio playback queue with sequential processing.
 *
 * Plays ArrayBuffer audio items one after another using a
 * persistent HTMLAudioElement with `preservesPitch` for proper
 * time-stretched speed control. Supports pause/resume, skip,
 * stop, and live speed changes.
 */
export class AudioQueue {
  private queue: AudioQueueItem[] = [];
  private currentAudio: HTMLAudioElement | null = null;
  private currentBlobUrl: string | null = null;
  private currentTag: string | undefined = undefined;
  private _isPlaying = false;
  private _isPaused = false;
  private _speed: number;
  private callbacks: AudioQueueCallbacks;
  private safetyTimeout: ReturnType<typeof setTimeout> | null = null;
  private playPromiseResolve: (() => void) | null = null;
  private _pendingSynthesis = 0;
  private _playedCount = 0;

  constructor(callbacks?: AudioQueueCallbacks) {
    this.callbacks = callbacks ?? {};
    const stored = parseFloat(
      localStorage.getItem(STORAGE_KEYS.PLAYBACK_SPEED) ?? '1',
    );
    this._speed = SPEED_STEPS.includes(stored as (typeof SPEED_STEPS)[number])
      ? stored
      : 1;
  }

  /** Whether the queue is currently playing */
  get isPlaying(): boolean {
    return this._isPlaying;
  }

  /** Whether playback is paused */
  get isPaused(): boolean {
    return this._isPaused;
  }

  /** Current playback speed */
  get speed(): number {
    return this._speed;
  }

  /** Number of items remaining in the queue */
  get length(): number {
    return this.queue.length;
  }

  /** Number of sentences currently being synthesized (not yet enqueued) */
  get pendingSynthesis(): number {
    return this._pendingSynthesis;
  }

  /**
   * Mark that a synthesis request is in flight.
   * Call before starting TTS synthesis so the queue knows not to
   * fire onPlaybackEnd while audio is still being prepared.
   */
  markSynthesisStart(): void {
    this._pendingSynthesis++;
  }

  /**
   * Mark that a synthesis request has completed (enqueued or failed).
   * When pending hits zero and the queue is empty, fires onPlaybackEnd.
   */
  markSynthesisEnd(): void {
    this._pendingSynthesis = Math.max(0, this._pendingSynthesis - 1);
    // If queue drained while synthesis was pending, fire end now
    if (this._pendingSynthesis === 0 && !this._isPlaying && this.queue.length === 0) {
      this.callbacks.onPlaybackEnd?.();
    }
  }

  /**
   * Enqueue an audio item for playback.
   * If nothing is currently playing, starts playback immediately.
   */
  enqueue(item: AudioQueueItem): void {
    this.queue.push(item);

    if (!this._isPlaying && !this._isPaused) {
      void this.playNext();
    }
  }

  /**
   * Stop all playback and clear the queue.
   *
   * Does NOT fire onPlaybackEnd — the caller (e.g. stopSpeaking)
   * is responsible for managing React state after an explicit stop.
   * Firing onPlaybackEnd synchronously here caused an infinite
   * React re-render loop (error #185) because the state change
   * triggered useEffects that called stopSpeaking again.
   */
  stop(): void {
    this.callbacks.onLog?.('queue_stop', { queueLength: this.queue.length, wasPlaying: this._isPlaying, pending: this._pendingSynthesis });
    this.queue = [];
    this.stopCurrentSource();
    this._isPlaying = false;
    this._isPaused = false;
    this._pendingSynthesis = 0;
    // Intentionally NOT calling onPlaybackEnd here — see JSDoc above.
  }

  /**
   * Pause playback. Unlike the old AudioBufferSourceNode approach,
   * HTMLAudioElement supports true pause — the current item can be
   * resumed from where it left off.
   */
  pause(): void {
    if (!this._isPlaying) return;
    this._isPaused = true;
    if (this.currentAudio && !this.currentAudio.paused) {
      this.currentAudio.pause();
    }
  }

  /**
   * Resume playback after pause. If an audio element is still
   * loaded (paused mid-playback), resume it. Otherwise continue
   * with the next queued item.
   */
  resume(): void {
    if (!this._isPaused) return;
    this._isPaused = false;

    // If we have a paused audio element, resume it directly
    if (this.currentAudio && this.currentAudio.paused && this.currentBlobUrl) {
      this.currentAudio.play().catch((err) => {
        console.warn('[audio] Resume play failed:', err);
      });
      return;
    }

    if (this.queue.length > 0) {
      void this.playNext();
    } else {
      this._isPlaying = false;
      this.callbacks.onPlaybackEnd?.();
    }
  }

  /**
   * Skip the currently playing item and move to the next.
   */
  skip(): void {
    if (!this._isPlaying) return;
    this.stopCurrentSource();
    // playNext will be triggered by the resolve in stopCurrentSource
  }

  /**
   * Set playback speed (1×–2×). Applies to the current and
   * future items. Uses HTMLAudioElement.preservesPitch for
   * proper time-stretching without pitch distortion.
   */
  setSpeed(speed: number): void {
    const clamped = Math.max(0.5, Math.min(2, speed));
    this._speed = clamped;
    localStorage.setItem(STORAGE_KEYS.PLAYBACK_SPEED, String(clamped));

    // Apply to currently playing audio element (live speed change)
    if (this.currentAudio) {
      this.currentAudio.playbackRate = clamped;
    }
  }

  /**
   * Cycle through preset speed steps.
   */
  cycleSpeed(): number {
    const idx = SPEED_STEPS.indexOf(
      this._speed as (typeof SPEED_STEPS)[number],
    );
    const nextSpeed = SPEED_STEPS[(idx + 1) % SPEED_STEPS.length]!;
    this.setSpeed(nextSpeed);
    return nextSpeed;
  }

  // ── Internal ──

  private async playNext(): Promise<void> {
    if (this._isPaused) return;

    if (this.queue.length === 0) {
      this._isPlaying = false;
      // Only fire onPlaybackEnd if no synthesis is in flight.
      // If sentences are still being synthesized, markSynthesisEnd()
      // will fire onPlaybackEnd when the last one resolves.
      if (this._pendingSynthesis === 0) {
        this.callbacks.onPlaybackEnd?.();
      }
      return;
    }

    const wasPlaying = this._isPlaying;
    this._isPlaying = true;

    if (!wasPlaying) {
      this.callbacks.onPlaybackStart?.();
    }

    const item = this.queue.shift()!;
    this.currentTag = item.tag;
    const log = this.callbacks.onLog;
    const itemIndex = this._playedCount++;

    try {
      await ensureAudioContextResumed();

      // Create a Blob URL from the raw audio ArrayBuffer
      const blob = new Blob([item.audioData], { type: 'audio/mpeg' });
      const blobUrl = URL.createObjectURL(blob);
      this.currentBlobUrl = blobUrl;

      // Use the persistent audio element for playback
      const audio = persistentAudio;
      audio.src = blobUrl;
      audio.playbackRate = this._speed;
      audio.preservesPitch = true;
      this.currentAudio = audio;

      this.callbacks.onItemStart?.(item.tag);
      log?.('play_start', { itemIndex, bytes: item.audioData.byteLength, queueRemaining: this.queue.length, pending: this._pendingSynthesis });

      await new Promise<void>((resolve) => {
        this.playPromiseResolve = resolve;

        const onEnded = () => {
          log?.('play_ended', { itemIndex, duration: audio.duration, currentTime: audio.currentTime });
          cleanup();
          resolve();
        };

        const onError = (e: Event) => {
          log?.('play_error', { itemIndex, error: String(e) });
          cleanup();
          // After error, reset the audio element so subsequent items can play
          try {
            audio.removeAttribute('src');
            audio.load();
          } catch { /* ignore */ }
          resolve();
        };

        const cleanup = () => {
          audio.removeEventListener('ended', onEnded);
          audio.removeEventListener('error', onError);
          if (this.safetyTimeout) {
            clearTimeout(this.safetyTimeout);
            this.safetyTimeout = null;
          }
          this.revokeCurrentBlobUrl();
          this.currentAudio = null;
          this.playPromiseResolve = null;
        };

        audio.addEventListener('ended', onEnded, { once: true });
        audio.addEventListener('error', onError, { once: true });

        // Safety timeout: resolve if ended/error never fire (mobile suspend).
        // We estimate duration from audio.duration once metadata loads,
        // but fall back to 60s if metadata isn't available yet.
        const startSafetyTimeout = () => {
          const estimatedDuration = audio.duration && isFinite(audio.duration)
            ? (audio.duration / this._speed) * 1000
            : 60000;
          log?.('play_safety_timer', { itemIndex, estimatedMs: estimatedDuration + 3000 });
          this.safetyTimeout = setTimeout(() => {
            log?.('play_safety_timeout', { itemIndex });
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('error', onError);
            this.revokeCurrentBlobUrl();
            this.currentAudio = null;
            this.playPromiseResolve = null;
            resolve();
          }, estimatedDuration + 3000);
        };

        // Start playback
        audio.play()
          .then(() => {
            startSafetyTimeout();
          })
          .catch((err) => {
            log?.('play_failed', { itemIndex, error: String(err) });
            cleanup();
            // After error, reset the audio element so subsequent items can play
            try {
              audio.removeAttribute('src');
              audio.load();
            } catch { /* ignore */ }
            resolve();
          });
      });

      this.callbacks.onItemEnd?.(item.tag);
    } catch (err) {
      log?.('play_catch', { itemIndex, error: String(err) });
      this.callbacks.onItemEnd?.(item.tag);
    }

    this.currentTag = undefined;

    // Continue with next item
    await this.playNext();
  }

  /** Revoke the current Blob URL to prevent memory leaks */
  private revokeCurrentBlobUrl(): void {
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }
  }

  private stopCurrentSource(): void {
    if (this.safetyTimeout) {
      clearTimeout(this.safetyTimeout);
      this.safetyTimeout = null;
    }

    if (this.currentAudio) {
      try {
        this.currentAudio.pause();
        this.currentAudio.removeAttribute('src');
        this.currentAudio.load(); // reset the element
      } catch {
        // Ignore — element might be in an unexpected state
      }
      this.currentAudio = null;
    }

    this.revokeCurrentBlobUrl();

    // Resolve any pending play promise so playNext can continue
    if (this.playPromiseResolve) {
      this.playPromiseResolve();
      this.playPromiseResolve = null;
    }

    this.callbacks.onItemEnd?.(this.currentTag);
  }
}
